// sseHook.ts
import { useEffect, useRef, useState } from 'react';
import REACT_APP_API_URL from '../ApiEnvironment';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../Store/store';
import { creditSlice } from '../../Store/creditSlice';
import eventManager from './ApiEventManager';
import { selectConfig } from '../../Store/configSlice';

export enum EventType {
    HEARTBEAT = 'heartbeat',
    CREDITS = 'credits',
}

interface EventProps {
    api?: any;
    userId?: string;
}

const useEvents = (props: EventProps) => {
    const { api, userId } = props;
    const dispatch = useDispatch<AppDispatch>();

    const config = useSelector(selectConfig);

    // State to track reconnection attempts
    const [reconnectAttempts, setReconnectAttempts] = useState(0);

    const lastHeartbeat = useRef<Date | null>(null);
    const heartbeatTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!userId || !api || config?.flags?.polling === undefined) return;

        let pollingInterval;

        if(config.flags?.polling || (process.env.REACT_APP_IS_RESELLER && reconnectAttempts > 0)) {

            pollingInterval = setInterval(() => {

                api.get(`${REACT_APP_API_URL}/events`).then((response) => {
                    const events = response.data;

                    if (!events || events.length === 0) return;

                    events.forEach((data: any) => {
                        eventManager.publish(data.type, data?.id, data.value);
                    });
                });
            }, 500);

            return;
        }

        let eventSource: EventSource | null = null;
        let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

        const checkHeartbeat = () => {
            // Check if last heartbeat received more than 15 seconds ago
            if (lastHeartbeat.current && new Date().getTime() - lastHeartbeat.current.getTime() > 15000) {
                console.log('No heartbeat received for 15 seconds, attempting to reconnect to EventSource...');
                setReconnectAttempts(reconnectAttempts + 1);
                eventSource?.close();
                connect(); // Trigger reconnection
            } else {
                // Schedule next heartbeat check
                heartbeatTimeoutRef.current = setTimeout(checkHeartbeat, 5000);
            }
        };

        const connect = async () =>
        {
            const url = `${ REACT_APP_API_URL }/events`;

            const authResponse = await api.get(`${url}/authenticate`);

            eventSource = new EventSource( `${url}/${authResponse.data.token}`, { withCredentials: true } );

            eventSource.onopen = () =>
            {
                setReconnectAttempts( 0 );
                lastHeartbeat.current = new Date(); // Reset heartbeat timestamp on open
                heartbeatTimeoutRef.current = setTimeout( checkHeartbeat, 5000 );
            };

            eventSource.onmessage = ( event ) =>
            {
                const data = JSON.parse( event.data );

                switch ( data.type ) {
                    case EventType.HEARTBEAT:
                        lastHeartbeat.current = new Date();
                        break;
                    case EventType.CREDITS:
                        dispatch( creditSlice.actions.setCreditAmount( data.value ) );
                        break;
                }
                eventManager.publish( data.type, data?.id, data.value );
            };

            eventSource.onerror = () =>
            {
                eventSource.close();
                eventSource = null;

                console.log('EventSource connection error, attempting to reconnect...');

                const delay = Math.min( 30, ( Math.pow( 2, reconnectAttempts ) - 1 ) ) * 1000;
                reconnectTimeout = setTimeout( () =>
                {
                    setReconnectAttempts( reconnectAttempts + 1 );
                    if(!process.env.REACT_APP_IS_RESELLER)
                        connect();
                }, delay );
            };
        };

        connect();

        // Listen to visibility change to optimize reconnections
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "visible" && !eventSource) {
                setReconnectAttempts(0);
                connect();
            }
        });

        return () => {
            eventSource?.close();
            if (reconnectTimeout) clearTimeout(reconnectTimeout);

            clearTimeout(pollingInterval)
        };
    }, [userId, api, reconnectAttempts, dispatch, config]);

};

export default useEvents;